import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  updateProfile 
} from 'firebase/auth';
import { db, auth } from '../config/firebaseConfig';
import logger from '../utils/logger';

// Service d'authentification
export const authService = {
  // Inscription d'un utilisateur
  async register(email, password, name) {
    try {
      // Créer l'utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Mettre à jour le profil avec le nom
      await updateProfile(user, { displayName: name });
      
      // Créer le document utilisateur dans Firestore
      const userRef = doc(db, 'users', user.uid);
      const now = new Date();
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      await setDoc(userRef, {
        email,
        name,
        subscriptionType: 'free',
        subscriptionStartDate: now,
        subscriptionEndDate: nextMonth,
        tournamentsCreated: 0,
        tournamentsLimit: 1,
        athletesLimit: 5,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        paidTournaments: [],
        oneTimePurchases: [],
        paymentHistory: [],
        createdAt: now,
        updatedAt: now
      });
      
      // Générer un token JWT (à implémenter côté backend)
      const token = await user.getIdToken();
      
      return { 
        token, 
        user: { 
          id: user.uid, 
          name, 
          email, 
          subscriptionType: 'free' 
        } 
      };
    } catch (error) {
      logger.error('Erreur lors de l\'inscription:', error);
      
      // Gérer les erreurs spécifiques de Firebase Auth
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Cette adresse email est déjà utilisée par un autre compte.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Le mot de passe est trop faible. Il doit contenir au moins 6 caractères.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('L\'adresse email n\'est pas valide.');
      }
      
      throw error;
    }
  },
  
  // Connexion d'un utilisateur
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Récupérer les données utilisateur depuis Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      // Variable pour stocker les données utilisateur
      let userData;
      
      if (!userDoc.exists()) {
        logger.log('Document utilisateur non trouvé, création d\'un nouveau document');
        const now = new Date();
        const nextMonth = new Date(now);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        
        await setDoc(userRef, {
          email: user.email,
          name: user.displayName || email.split('@')[0],
          createdAt: now,
          updatedAt: now,
          subscriptionType: 'free',
          subscriptionEndDate: now,
          tournamentsCreated: 0,
          tournamentsLimit: 0,
          paidTournaments: []
        });
        
        // Récupérer le document nouvellement créé
        const newUserDoc = await getDoc(userRef);
        userData = newUserDoc.data();
      } else {
        userData = userDoc.data();
      }
      
      // Générer un token JWT
      const token = await user.getIdToken();
      
      return { 
        token, 
        user: { 
          id: user.uid, 
          name: userData.name || user.displayName, 
          email: user.email, 
          subscriptionType: userData.subscriptionType || 'free' 
        } 
      };
    } catch (error) {
      logger.error('Erreur lors de la connexion:', error);
      
      // Gérer les erreurs spécifiques de Firebase Auth
      if (error.code === 'auth/user-not-found') {
        throw new Error('Aucun utilisateur trouvé avec cette adresse email.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Mot de passe incorrect.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('L\'adresse email n\'est pas valide.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Trop de tentatives de connexion. Veuillez réessayer plus tard.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('Ce compte a été désactivé.');
      } else if (error.message && error.message.includes('Missing or insufficient permissions')) {
        throw new Error('Problème d\'accès à la base de données. Veuillez contacter l\'administrateur.');
      }
      
      throw error;
    }
  },
  
  // Déconnexion
  async logout() {
    try {
      await signOut(auth);
      return { success: true, message: 'Déconnexion réussie' };
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      throw error;
    }
  },
  
  // Réauthentification d'un utilisateur à partir de son ID
  // Cette méthode est utilisée pour réauthentifier un utilisateur après redirection Stripe
  async reauthenticateWithUserId(userId) {
    try {
      console.log('Tentative de réauthentification avec ID utilisateur:', userId);
      
      // Vérifier si l'utilisateur existe dans Firestore
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Utilisateur non trouvé dans Firestore');
      }
      
      // Pour une véritable réauthentification, il faudrait utiliser un token personnalisé
      // ou une autre méthode sécurisée. Cette implémentation est simplifiée pour le test.
      
      // Forcer la persistance de l'ID utilisateur dans le localStorage de Firebase
      localStorage.setItem('firebase:authUser:AIzaSyBqDzwvVqGILnCJzPQAqBGcbI5k_XlSPPY:[DEFAULT]', JSON.stringify({
        uid: userId,
        displayName: userDoc.data().name || '',
        email: userDoc.data().email || '',
        emailVerified: true
      }));
      
      // Rafraîchir la page pour forcer Firebase à récupérer l'utilisateur du localStorage
      // Note: Ceci est une solution temporaire, une meilleure approche serait d'utiliser
      // les méthodes d'authentification Firebase appropriées
      
      return { success: true, message: 'Réauthentification réussie' };
    } catch (error) {
      console.error('Erreur lors de la réauthentification:', error);
      throw error;
    }
  },
  
  // Vérifier l'état de l'authentification
  onAuthStateChanged(callback) {
    return auth.onAuthStateChanged(callback);
  },
  
  // Obtenir l'utilisateur courant
  getCurrentUser() {
    return auth.currentUser;
  }
};

// Service utilisateur
export const userService = {
  // Obtenir le profil utilisateur
  async getUserProfile() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('Profil utilisateur non trouvé');
      }
      
      const userData = userDoc.data();
      
      // Vérifier si l'abonnement est actif
      let isSubscriptionActive = userData.subscriptionType !== 'free';
      
      // Si nous avons une date d'expiration, vérifier si elle est dans le futur
      if (userData.subscriptionEndDate) {
        try {
          // Essayer de convertir la date (peut être un timestamp Firestore ou une chaîne)
          let endDate;
          if (userData.subscriptionEndDate.toDate && typeof userData.subscriptionEndDate.toDate === 'function') {
            endDate = userData.subscriptionEndDate.toDate();
          } else if (userData.subscriptionEndDate.seconds) {
            endDate = new Date(userData.subscriptionEndDate.seconds * 1000);
          } else {
            endDate = new Date(userData.subscriptionEndDate);
          }
          
          // Vérifier si la date est valide et dans le futur
          if (!isNaN(endDate.getTime()) && endDate > new Date()) {
            isSubscriptionActive = true;
          } else {
            // Si la date d'expiration est passée, l'abonnement n'est plus actif
            isSubscriptionActive = false;
          }
          
          // Ajouter la date d'expiration formatée pour faciliter l'affichage
          userData.formattedEndDate = endDate.toLocaleDateString();
        } catch (err) {
          logger.warn('Erreur lors de la conversion de la date d\'expiration:', err);
          isSubscriptionActive = false;
        }
      }
      
      // S'assurer que les limites sont définies
      if (userData.subscriptionType === 'free' && !userData.tournamentsLimit) {
        userData.tournamentsLimit = 1;
      }
      if (userData.subscriptionType === 'free' && !userData.athletesLimit) {
        userData.athletesLimit = 5;
      }
      
      return {
        user: {
          ...userData,
          id: user.uid,
          isSubscriptionActive
        }
      };
    } catch (error) {
      logger.error('Erreur lors de la récupération du profil:', error);
      throw error;
    }
  },
  
  // Mettre à jour le profil utilisateur
  async updateProfile(updateData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Profil utilisateur non trouvé');
      }
      
      // Mettre à jour le document utilisateur
      await updateDoc(userRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      // Récupérer le profil mis à jour
      return await this.getUserProfile();
    } catch (error) {
      logger.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  },
  
  // Vérifier l'éligibilité pour créer un tournoi
  async checkTournamentEligibility() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('Profil utilisateur non trouvé');
      }
      
      const userData = userDoc.data();
      
      // Vérifier si l'abonnement est actif
      let isSubscriptionActive = userData.isSubscriptionActive || userData.plan_actif || false;
      
      // Si nous avons une date d'expiration, vérifier si elle est dans le futur
      if (userData.subscriptionEndDate) {
        try {
          // Essayer de convertir la date (peut être un timestamp Firestore ou une chaîne)
          let endDate;
          if (userData.subscriptionEndDate.toDate && typeof userData.subscriptionEndDate.toDate === 'function') {
            endDate = userData.subscriptionEndDate.toDate();
          } else {
            endDate = new Date(userData.subscriptionEndDate);
          }
          
          // Vérifier si la date est valide et dans le futur
          if (!isNaN(endDate.getTime()) && endDate > new Date()) {
            isSubscriptionActive = true;
          }
        } catch (err) {
          logger.warn('Erreur lors de la conversion de la date d\'expiration:', err);
        }
      }
      
      // Vérifier si l'utilisateur a atteint sa limite de tournois
      const tournamentsCreated = userData.tournamentsCreated || 0;
      let tournamentsLimit = userData.tournamentsLimit || 0;
      
      // Définir les limites par défaut en fonction du type d'abonnement si elles ne sont pas définies
      if (tournamentsLimit === 0) {
        switch (userData.subscriptionType) {
          case 'basic':
            tournamentsLimit = 3;
            break;
          case 'premium':
            tournamentsLimit = 10;
            break;
          case 'unlimited':
            tournamentsLimit = Number.MAX_SAFE_INTEGER; // Illimité
            break;
          default:
            tournamentsLimit = 1; // Plan gratuit ou inconnu
        }
      }
      
      const hasReachedLimit = tournamentsCreated >= tournamentsLimit;
      
      // Vérifier si l'utilisateur a déjà payé pour un nouveau tournoi
      const hasPaidForNewTournament = userData.paidTournaments && userData.paidTournaments.length > 0;
      
      // Déterminer si l'utilisateur peut créer un tournoi
      const canCreateTournament = (isSubscriptionActive && !hasReachedLimit) || hasPaidForNewTournament;
      
      return {
        isSubscriptionActive,
        hasReachedLimit,
        canCreateTournament,
        hasPaidForNewTournament,
        subscriptionType: userData.subscriptionType || 'free',
        tournamentsCreated: tournamentsCreated,
        tournamentsLimit: tournamentsLimit,
        subscriptionEndDate: userData.subscriptionEndDate
      };
    } catch (error) {
      logger.error('Erreur lors de la vérification d\'éligibilité:', error);
      throw error;
    }
  },
  
  // Mettre à jour le statut de paiement
  async updatePaymentStatus(sessionId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error('Profil utilisateur non trouvé');
      }
      
      const userData = userDoc.data();
      
      // Vérifier si la session existe déjà dans les paiements
      const alreadyPaid = userData.paidTournaments && userData.paidTournaments.includes(sessionId);
      
      if (!alreadyPaid) {
        // Préparer les mises à jour
        const now = new Date();
        const updates = {
          updatedAt: now,
          plan_actif: true // Mettre à jour le champ plan_actif à true
        };
        
        // Ajouter la session à la liste des tournois payés
        const paidTournaments = [...(userData.paidTournaments || []), sessionId];
        updates.paidTournaments = paidTournaments;
        
        // Ajouter aux achats à l'unité
        const oneTimePurchases = [...(userData.oneTimePurchases || []), {
          purchaseDate: now,
          amount: 10, // 10€ pour un tournoi
          stripeSessionId: sessionId
        }];
        updates.oneTimePurchases = oneTimePurchases;
        
        // Ajouter à l'historique des paiements
        const paymentHistory = [...(userData.paymentHistory || []), {
          type: 'tournament',
          amount: 10,
          date: now,
          status: 'succeeded',
          description: 'Paiement pour un nouveau tournoi'
        }];
        updates.paymentHistory = paymentHistory;
        
        // Mettre à jour l'utilisateur
        await updateDoc(userRef, updates);
      }
      
      return { 
        success: true, 
        message: alreadyPaid ? 'Paiement déjà enregistré' : 'Statut de paiement mis à jour avec succès',
        hasPaidForNewTournament: true
      };
    } catch (error) {
      logger.error('Erreur lors de la mise à jour du statut de paiement:', error);
      throw error;
    }
  },
  
  // Mettre à jour l'abonnement de l'utilisateur
  async updateSubscription(planType, duration = 1) {
    try {
      // Normaliser le type de plan pour éviter les incohérences
      let normalizedPlanType = planType ? planType.toLowerCase() : 'free';
      
      // Vérifier que le plan est valide
      if (!['basic', 'premium', 'gala', 'free'].includes(normalizedPlanType)) {
        console.warn(`Type de plan non reconnu: ${planType}, utilisation du plan basic par défaut`);
        normalizedPlanType = 'basic';
      }
      
      console.log('Début updateSubscription avec plan normalisé:', normalizedPlanType, 'et durée:', duration);
      
      const user = auth.currentUser;
      console.log('Utilisateur authentifié:', !!user);
      
      if (!user) {
        console.error('Erreur: Utilisateur non connecté');
        throw new Error('Utilisateur non connecté');
      }
      
      console.log('ID utilisateur pour mise à jour Firestore:', user.uid);
      
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.error('Erreur: Profil utilisateur non trouvé');
        throw new Error('Profil utilisateur non trouvé');
      }
      
      console.log('Document utilisateur trouvé:', userDoc.exists());
      
      // Définir les limites en fonction du type d'abonnement
      let tournamentsLimit = 0;
      let athletesLimit = 0;
      let subscriptionPrice = 0;
      
      // Calculer la date de fin d'abonnement
      const now = new Date();
      // Déclarer subscriptionEndDate AVANT son utilisation dans le switch
      let subscriptionEndDate = new Date(now);
      
      switch (normalizedPlanType) {
        case 'basic':
          tournamentsLimit = 3;
          athletesLimit = 50;
          subscriptionPrice = 9.99;
          // Durée standard en mois
          subscriptionEndDate.setMonth(subscriptionEndDate.getMonth() + duration);
          break;
        case 'premium':
          tournamentsLimit = 10;
          athletesLimit = 200;
          subscriptionPrice = 19.99;
          // Durée standard en mois
          subscriptionEndDate.setMonth(subscriptionEndDate.getMonth() + duration);
          break;
        case 'gala':
          tournamentsLimit = 2;
          athletesLimit = 150;
          subscriptionPrice = 24.99;
          // Pour le plan gala, on définit une durée d'une semaine au lieu d'un mois
          subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 7); // 7 jours au lieu de 30
          break;
        default: // 'free'
          tournamentsLimit = 1;
          athletesLimit = 5;
          subscriptionPrice = 0;
          // Durée standard en mois pour le plan gratuit aussi
          subscriptionEndDate.setMonth(subscriptionEndDate.getMonth() + duration);
      }
      
      // Préparer les mises à jour
      const updates = {
        subscriptionType: planType,
        subscriptionStartDate: now,
        subscriptionEndDate,
        tournamentsLimit,
        athletesLimit,
        updatedAt: now,
        plan_actif: true
      };
      
      // Ajouter à l'historique des paiements
      const userData = userDoc.data();
      const paymentHistory = [...(userData.paymentHistory || []), {
        type: 'subscription',
        amount: subscriptionPrice * duration,
        date: now,
        status: 'succeeded',
        description: `Abonnement ${planType} pour ${duration} mois`
      }];
      updates.paymentHistory = paymentHistory;
      
      // Mettre à jour l'utilisateur
      console.log('Mise à jour Firestore avec les données suivantes:', JSON.stringify(updates));
      console.log('Type de plan envoyé à Firestore:', planType);
      
      await updateDoc(userRef, updates);
      
      // Vérifier que la mise à jour a bien été appliquée
      const updatedDoc = await getDoc(userRef);
      const updatedData = updatedDoc.data();
      console.log('Données utilisateur après mise à jour:', {
        subscriptionType: updatedData.subscriptionType,
        plan_actif: updatedData.plan_actif
      });
      
      return {
        success: true,
        message: 'Abonnement mis à jour avec succès',
        subscription: {
          type: planType,
          startDate: now,
          endDate: subscriptionEndDate,
          tournamentsLimit,
          athletesLimit
        }
      };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'abonnement:', error);
      throw error;
    }
  },
  
  // Vérifier si un utilisateur peut accéder à une fonctionnalité
  async checkPlanAccess(feature, quantity = 1) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        throw new Error('Profil utilisateur non trouvé');
      }
      
      const userData = userDoc.data();
      
      // Vérifier si l'utilisateur a un plan actif
      const hasPlanActif = userData.plan_actif === true;
      
      // Vérifier si l'abonnement est actif
      let isSubscriptionActive = userData.isSubscriptionActive || userData.plan_actif || false;
      
      // Si nous avons une date d'expiration, vérifier si elle est dans le futur
      if (userData.subscriptionEndDate) {
        try {
          // Essayer de convertir la date (peut être un timestamp Firestore ou une chaîne)
          let endDate;
          if (userData.subscriptionEndDate.toDate && typeof userData.subscriptionEndDate.toDate === 'function') {
            endDate = userData.subscriptionEndDate.toDate();
          } else {
            endDate = new Date(userData.subscriptionEndDate);
          }
          
          // Vérifier si la date est valide et dans le futur
          if (!isNaN(endDate.getTime()) && endDate > new Date()) {
            isSubscriptionActive = true;
          }
        } catch (err) {
          logger.warn('Erreur lors de la conversion de la date d\'expiration:', err);
        }
      }
      
      // Résultat par défaut
      let result = {
        hasAccess: false,
        reason: 'Abonnement inactif',
        currentUsage: 0,
        limit: 0
      };
      
      // Vérifier l'accès en fonction de la fonctionnalité
      switch (feature) {
        case 'create_tournament':
          // Vérifier si l'utilisateur a payé pour des tournois individuels
          const hasPaidTournaments = userData.paidTournaments && userData.paidTournaments.length > 0;
          
          if (hasPaidTournaments) {
            result = {
              hasAccess: true,
              reason: 'Paiement individuel',
              currentUsage: 0,
              limit: userData.paidTournaments.length
            };
          } else if (isSubscriptionActive) {
            const currentTournaments = userData.tournamentsCreated || 0;
            let tournamentLimit = userData.tournamentsLimit || 0;
            
            // Définir les limites par défaut en fonction du type d'abonnement si elles ne sont pas définies
            if (tournamentLimit === 0) {
              switch (userData.subscriptionType) {
                case 'basic':
                  tournamentLimit = 3;
                  break;
                case 'premium':
                  tournamentLimit = 10;
                  break;
                case 'unlimited':
                  tournamentLimit = Number.MAX_SAFE_INTEGER; // Illimité
                  break;
                default:
                  tournamentLimit = 1; // Plan gratuit ou inconnu
              }
            }
            
            result = {
              hasAccess: currentTournaments + quantity <= tournamentLimit,
              reason: result.hasAccess ? 'Abonnement actif' : 'Limite de tournois atteinte',
              currentUsage: currentTournaments,
              limit: tournamentLimit
            };
          }
          break;
          
        case 'add_athletes':
          if (isSubscriptionActive || hasPlanActif) {
            const currentAthletes = userData.athletesCount || 0;
            let athleteLimit = userData.athletesLimit || 0;
            
            // Définir les limites par défaut en fonction du type d'abonnement si elles ne sont pas définies
            if (athleteLimit === 0) {
              switch (userData.subscriptionType) {
                case 'basic':
                  athleteLimit = 50;
                  break;
                case 'premium':
                  athleteLimit = 200;
                  break;
                case 'unlimited':
                  athleteLimit = Number.MAX_SAFE_INTEGER; // Illimité
                  break;
                default:
                  athleteLimit = 5; // Plan gratuit ou inconnu
              }
            }
            
            result = {
              hasAccess: currentAthletes + quantity <= athleteLimit,
              reason: result.hasAccess ? 'Abonnement actif' : 'Limite d\'athlètes atteinte',
              currentUsage: currentAthletes,
              limit: athleteLimit
            };
          }
          break;
          
        case 'advanced_features':
          // Fonctionnalités avancées disponibles uniquement avec les abonnements premium et unlimited
          if (isSubscriptionActive && (userData.subscriptionType === 'premium' || userData.subscriptionType === 'unlimited')) {
            result = {
              hasAccess: true,
              reason: 'Abonnement premium ou unlimited actif',
              currentUsage: 0,
              limit: 0
            };
          } else {
            result = {
              hasAccess: false,
              reason: 'Abonnement premium ou unlimited requis',
              currentUsage: 0,
              limit: 0
            };
          }
          break;
          
        default:
          result = {
            hasAccess: false,
            reason: 'Fonctionnalité inconnue',
            currentUsage: 0,
            limit: 0
          };
      }
      
      return result;
    } catch (error) {
      logger.error('Erreur lors de la vérification de l\'accès au plan:', error);
      throw error;
    }
  },
};

// Service tournoi
export const tournamentService = {
  // Créer un tournoi
  async createTournament(tournamentData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Vérifier l'éligibilité
      const eligibility = await userService.checkTournamentEligibility();
      if (!eligibility.canCreateTournament) {
        throw new Error('Vous n\'avez pas d\'abonnement actif ou vous avez atteint votre limite de tournois');
      }
      
      // Si l'utilisateur a payé pour ce tournoi, retirer un tournoi de sa liste de tournois payés
      if (eligibility.hasPaidForNewTournament && !eligibility.isSubscriptionActive) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        
        const paidTournaments = [...userData.paidTournaments];
        paidTournaments.pop(); // Retirer le dernier tournoi payé
        
        await updateDoc(userRef, {
          paidTournaments,
          updatedAt: new Date()
        });
      }
      
      // Créer le tournoi
      const now = new Date();
      
      // S'assurer que les disciplines sont un tableau
      if (!Array.isArray(tournamentData.disciplines)) {
        tournamentData.disciplines = [];
      }
      
      const newTournament = {
        ...tournamentData,
        createdBy: user.uid,
        athletes: [],
        createdAt: now,
        updatedAt: now
      };
      
      const tournamentRef = await addDoc(collection(db, 'tournaments'), newTournament);
      
      // Incrémenter le compteur de tournois de l'utilisateur
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      await updateDoc(userRef, {
        tournamentsCreated: (userData.tournamentsCreated || 0) + 1,
        updatedAt: now
      });
      
      return { id: tournamentRef.id, ...newTournament };
    } catch (error) {
      logger.error('Erreur lors de la création du tournoi:', error);
      throw error;
    }
  },
  
  // Récupérer tous les tournois de l'utilisateur connecté
  async getAllTournaments() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Ne récupérer que les tournois créés par l'utilisateur connecté
      // Solution temporaire pour éviter l'erreur d'index manquant
      // Une fois l'index créé, vous pourrez réactiver le tri par date
      const tournamentsSnapshot = await getDocs(
        query(
          collection(db, 'tournaments'), 
          where('createdBy', '==', user.uid)
          // orderBy('createdAt', 'desc') // Commenté temporairement jusqu'à la création de l'index
        )
      );
      
      // Tri manuel des résultats
      const tournamentDocs = tournamentsSnapshot.docs.sort((a, b) => {
        const dateA = a.data().createdAt;
        const dateB = b.data().createdAt;
        
        // Convertir les timestamps en millisecondes pour comparaison
        const timeA = dateA && typeof dateA.toMillis === 'function' ? dateA.toMillis() : new Date(dateA).getTime();
        const timeB = dateB && typeof dateB.toMillis === 'function' ? dateB.toMillis() : new Date(dateB).getTime();
        
        return timeB - timeA; // Tri décroissant (plus récent d'abord)
      });
      
      return tournamentDocs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function' ? data.updatedAt.toDate() : new Date(data.updatedAt)
        };
      });
    } catch (error) {
      logger.error('Erreur lors de la récupération des tournois:', error);
      throw error;
    }
  },
  
  // Récupérer un tournoi par son ID
  async getTournamentById(tournamentId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        return null;
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est bien le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à ce tournoi');
      }
      
      // Récupérer les athlètes associés au tournoi
      const athletes = [];
      if (tournamentData.athletes && tournamentData.athletes.length > 0) {
        // Récupérer chaque athlète individuellement car Firestore ne permet pas
        // d'utiliser 'where' avec l'ID du document
        for (const athleteId of tournamentData.athletes) {
          try {
            const athleteDoc = await getDoc(doc(db, 'athletes', athleteId));
            if (athleteDoc.exists()) {
              const athleteData = athleteDoc.data();
              athletes.push({
                id: athleteId,
                ...athleteData,
                createdAt: athleteData.createdAt && typeof athleteData.createdAt.toDate === 'function' ? athleteData.createdAt.toDate() : new Date(athleteData.createdAt),
                updatedAt: athleteData.updatedAt && typeof athleteData.updatedAt.toDate === 'function' ? athleteData.updatedAt.toDate() : new Date(athleteData.updatedAt)
              });
            }
          } catch (err) {
            logger.error(`Erreur lors de la récupération de l'athlète ${athleteId}:`, err);
          }
        }
      }
      
      return {
        id: tournamentDoc.id,
        ...tournamentData,
        createdAt: tournamentData.createdAt.toDate(),
        updatedAt: tournamentData.updatedAt.toDate(),
        athletes
      };
    } catch (error) {
      logger.error('Erreur lors de la récupération du tournoi:', error);
      throw error;
    }
  },
  
  // Mettre à jour un tournoi
  async updateTournament(tournamentId, updateData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'êtes pas autorisé à modifier ce tournoi');
      }
      
      // Mettre à jour le tournoi
      await updateDoc(tournamentRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      // Récupérer le tournoi mis à jour
      const updatedTournamentDoc = await getDoc(tournamentRef);
      const updatedTournamentData = updatedTournamentDoc.data();
      
      return {
        id: tournamentId,
        ...updatedTournamentData,
        createdAt: updatedTournamentData.createdAt.toDate(),
        updatedAt: updatedTournamentData.updatedAt.toDate()
      };
    } catch (error) {
      logger.error('Erreur lors de la mise à jour du tournoi:', error);
      throw error;
    }
  },
  
  
  // Supprimer un tournoi
  async deleteTournament(tournamentId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'êtes pas autorisé à supprimer ce tournoi');
      }
      
      // Supprimer le tournoi
      await deleteDoc(tournamentRef);
      
      return { success: true, message: 'Tournoi supprimé avec succès' };
    } catch (error) {
      logger.error('Erreur lors de la suppression du tournoi:', error);
      throw error;
    }
  },
  
  // Générer des matchs pour un tournoi
  async generateMatches(tournamentId, discipline, options = {}) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Vérifier que le tournoi existe
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à ce tournoi');
      }
      
      // Vérifier que le tournoi a des athlètes
      if (!tournamentData.athletes || tournamentData.athletes.length < 2) {
        throw new Error('Le tournoi doit avoir au moins 2 athlètes pour générer des matchs.');
      }
      
      // Récupérer les athlètes du tournoi
      const athletes = [];
      for (const athleteId of tournamentData.athletes) {
        try {
          const athleteDoc = await getDoc(doc(db, 'athletes', athleteId));
          if (athleteDoc.exists()) {
            athletes.push({
              id: athleteId,
              ...athleteDoc.data()
            });
          }
        } catch (err) {
          logger.error(`Erreur lors de la récupération de l'athlète ${athleteId}:`, err);
        }
      }
      
      // Grouper les athlètes par catégorie (genre et poids)
      const categories = {};
      
      athletes.forEach(athlete => {
        // Déterminer la catégorie de poids
        let weightCategory;
        const weight = parseFloat(athlete.weight);
        const gender = athlete.gender;
        
        if (gender === 'Homme') {
          if (weight < 60) weightCategory = '-60kg';
          else if (weight < 66) weightCategory = '-66kg';
          else if (weight < 73) weightCategory = '-73kg';
          else if (weight < 81) weightCategory = '-81kg';
          else if (weight < 90) weightCategory = '-90kg';
          else if (weight < 100) weightCategory = '-100kg';
          else weightCategory = '+100kg';
        } else if (gender === 'Femme') {
          if (weight < 48) weightCategory = '-48kg';
          else if (weight < 52) weightCategory = '-52kg';
          else if (weight < 57) weightCategory = '-57kg';
          else if (weight < 63) weightCategory = '-63kg';
          else if (weight < 70) weightCategory = '-70kg';
          else if (weight < 78) weightCategory = '-78kg';
          else weightCategory = '+78kg';
        } else {
          // Catégorie par défaut pour les autres genres
          if (weight < 70) weightCategory = 'Léger';
          else weightCategory = 'Lourd';
        }
        
        const category = `${gender} ${weightCategory}`;
        
        if (!categories[category]) {
          categories[category] = [];
        }
        
        categories[category].push(athlete);
      });
      
      // Générer les matchs pour chaque catégorie
      const allMatches = [];
      const now = new Date();
      
      for (const [category, categoryAthletes] of Object.entries(categories)) {
        // S'il n'y a qu'un seul athlète dans la catégorie, il est exempt
        if (categoryAthletes.length === 1) {
          const match = {
            tournamentId,
            discipline,
            category,
            round: 1,
            phase: 'Finale', // Ajouter la phase "Finale" pour un seul athlète
            athlete1: categoryAthletes[0].id,
            athlete2: null,
            winner: categoryAthletes[0].id,
            status: 'bye',
            createdAt: now,
            updatedAt: now,
            createdBy: user.uid
          };
          
          const matchRef = await addDoc(collection(db, 'matches'), match);
          allMatches.push({ id: matchRef.id, ...match });
          continue;
        }
        
        // Mélanger les athlètes pour un tirage aléatoire
        const shuffledAthletes = [...categoryAthletes].sort(() => Math.random() - 0.5);
        
        // Générer les matchs du premier tour
        // Déterminer le nombre de tatamis disponibles
        const tatamiCount = tournamentData.tatamiCount || 1;
        
        // Récupérer les options de configuration
        const {
          startTime = '09:00',
          matchDuration = 15,
          distributeByTatami = true
        } = options;
        
        // Convertir l'heure de début en objet Date
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const baseTime = new Date();
        baseTime.setHours(startHour || 9, startMinute || 0, 0, 0);
        
        // Structure pour suivre les horaires des athlètes
        const athleteSchedule = {};
        
        // Compteurs pour le suivi des matchs par tatami
        const tatamiMatchCounts = {};
        for (let i = 1; i <= tatamiCount; i++) {
          tatamiMatchCounts[i] = 0;
        }
        
        // Déterminer la phase du tournoi en fonction du nombre d'athlètes
        let tournamentPhase = '';
        const athleteCount = shuffledAthletes.length;
        
        if (athleteCount <= 2) {
          tournamentPhase = 'Finale';
        } else if (athleteCount <= 4) {
          tournamentPhase = 'Demi-finale';
        } else if (athleteCount <= 8) {
          tournamentPhase = 'Quart de finale';
        } else if (athleteCount <= 16) {
          tournamentPhase = 'Huitième de finale';
        } else if (athleteCount <= 32) {
          tournamentPhase = 'Seizième de finale';
        } else {
          tournamentPhase = 'Tour préliminaire';
        }
        
        // Créer des paires d'athlètes pour les matchs
        const matches = [];
        for (let i = 0; i < shuffledAthletes.length; i += 2) {
          const athlete1 = shuffledAthletes[i];
          const athlete2 = i + 1 < shuffledAthletes.length ? shuffledAthletes[i + 1] : null;
          
          if (athlete1 && athlete2) {
            matches.push({ athlete1, athlete2, phase: tournamentPhase });
          } else if (athlete1) {
            // Match avec un seul athlète (bye)
            matches.push({ athlete1, athlete2: null, phase: tournamentPhase });
          }
        }
        
        // Compteur global pour les matchs
        let globalMatchIndex = 0;
        
        // Planifier les matchs en évitant les conflits d'horaire
        // Trier les matchs par catégorie pour une meilleure organisation
        const sortedMatches = [...matches].sort((a, b) => {
          // Priorité aux matchs avec deux athlètes
          if (a.athlete2 && !b.athlete2) return -1;
          if (!a.athlete2 && b.athlete2) return 1;
          return 0;
        });
        
        // Créer un tableau de créneaux horaires par tatami
        const tatamiTimeSlots = {};
        for (let i = 1; i <= tatamiCount; i++) {
          tatamiTimeSlots[i] = [];
        }
        
        for (const matchPair of sortedMatches) {
          const { athlete1, athlete2 } = matchPair;
          
          // Trouver le meilleur tatami et créneau pour ce match
          let bestTatami = 1;
          let bestTimeSlot = 0;
          let scheduledTime = '';
          let foundValidSlot = false;
          
          // Commencer à chercher depuis le début des créneaux
          timeSlotLoop: for (let timeSlot = 0; timeSlot < 100; timeSlot++) { // Limite arbitraire pour éviter les boucles infinies
            // Calculer l'heure pour ce créneau
            const matchTime = new Date(baseTime);
            matchTime.setMinutes(matchTime.getMinutes() + (timeSlot * matchDuration));
            
            // Formater l'heure au format HH:MM
            scheduledTime = matchTime.getHours().toString().padStart(2, '0') + ':' + 
                          matchTime.getMinutes().toString().padStart(2, '0');
            
            // Vérifier si les athlètes sont déjà occupés à cette heure
            const athlete1Busy = athleteSchedule[athlete1.id] && athleteSchedule[athlete1.id].includes(scheduledTime);
            const athlete2Busy = athlete2 && athleteSchedule[athlete2.id] && athleteSchedule[athlete2.id].includes(scheduledTime);
            
            if (athlete1Busy || athlete2Busy) {
              // Les athlètes sont occupés, essayer le créneau suivant
              continue;
            }
            
            // Trouver le tatami le moins utilisé pour ce créneau
            if (distributeByTatami) {
              // Vérifier chaque tatami pour ce créneau horaire
              for (let t = 1; t <= tatamiCount; t++) {
                // Vérifier si ce tatami est libre à cette heure
                const tatamiOccupied = tatamiTimeSlots[t].includes(scheduledTime);
                
                if (!tatamiOccupied) {
                  bestTatami = t;
                  bestTimeSlot = timeSlot;
                  foundValidSlot = true;
                  break timeSlotLoop; // Sortir des deux boucles
                }
              }
            } else {
              // Mode séquentiel: utiliser toujours le tatami 1
              const tatamiOccupied = tatamiTimeSlots[1].includes(scheduledTime);
              
              if (!tatamiOccupied) {
                bestTatami = 1;
                bestTimeSlot = timeSlot;
                foundValidSlot = true;
                break; // Sortir de la boucle
              }
            }
          }
          
          // Si aucun créneau n'a été trouvé, utiliser le dernier créneau disponible
          if (!foundValidSlot) {
            // Trouver le dernier créneau utilisé pour chaque tatami
            const lastTimeSlots = {};
            for (let t = 1; t <= tatamiCount; t++) {
              const times = tatamiTimeSlots[t].map(time => {
                const [hours, minutes] = time.split(':').map(Number);
                return hours * 60 + minutes;
              });
              lastTimeSlots[t] = times.length > 0 ? Math.max(...times) : 0;
            }
            
            // Trouver le tatami avec le dernier créneau le plus tôt
            bestTatami = 1;
            for (let t = 2; t <= tatamiCount; t++) {
              if (lastTimeSlots[t] < lastTimeSlots[bestTatami]) {
                bestTatami = t;
              }
            }
            
            // Calculer le prochain créneau disponible pour ce tatami
            const nextTimeSlot = Math.ceil(lastTimeSlots[bestTatami] / matchDuration);
            bestTimeSlot = nextTimeSlot;
            
            // Calculer l'heure pour ce créneau
            const matchTime = new Date(baseTime);
            matchTime.setMinutes(matchTime.getMinutes() + (bestTimeSlot * matchDuration));
            
            // Formater l'heure au format HH:MM
            scheduledTime = matchTime.getHours().toString().padStart(2, '0') + ':' + 
                          matchTime.getMinutes().toString().padStart(2, '0');
          }
          
          // Marquer le tatami comme occupé à cette heure
          tatamiTimeSlots[bestTatami].push(scheduledTime);
          
          // Marquer les athlètes comme occupés à cette heure
          if (!athleteSchedule[athlete1.id]) {
            athleteSchedule[athlete1.id] = [];
          }
          athleteSchedule[athlete1.id].push(scheduledTime);
          
          if (athlete2) {
            if (!athleteSchedule[athlete2.id]) {
              athleteSchedule[athlete2.id] = [];
            }
            athleteSchedule[athlete2.id].push(scheduledTime);
          }
          
          // Mettre à jour les variables pour ce match
          matchPair.scheduledTime = scheduledTime;
          matchPair.tatamiNumber = bestTatami;
          matchPair.timeSlot = bestTimeSlot;
          
          // Incrémenter le compteur de matchs pour ce tatami
          tatamiMatchCounts[bestTatami] = (tatamiMatchCounts[bestTatami] || 0) + 1;
          
          // Incrémenter le compteur global pour le prochain match
          globalMatchIndex++;
          
          const match = {
            tournamentId,
            discipline,
            category,
            round: 1,
            phase: matchPair.phase, // Ajouter la phase du tournoi
            athlete1: athlete1.id,
            athlete2: athlete2 ? athlete2.id : null,
            winner: athlete2 ? null : athlete1.id,
            status: athlete2 ? 'scheduled' : 'bye',
            tatamiNumber: bestTatami,
            scheduledTime: scheduledTime,
            createdAt: now,
            updatedAt: now,
            createdBy: user.uid
          };
          
          const matchRef = await addDoc(collection(db, 'matches'), match);
          allMatches.push({ id: matchRef.id, ...match });
        }
      }
      
      // Mettre à jour le tournoi pour indiquer que les matchs ont été générés
      await updateDoc(tournamentRef, {
        matchesGenerated: true,
        updatedAt: now
      });
      
      return allMatches;
    } catch (error) {
      logger.error('Erreur lors de la génération des matchs:', error);
      throw error;
    }
  },
  
  // Récupérer les matchs d'un tournoi
  async getMatchesByTournament(tournamentId, discipline) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Vérifier que le tournoi existe
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à ce tournoi');
      }
      
      // Récupérer les matchs du tournoi pour la discipline spécifiée
      const matchesQuery = query(
        collection(db, 'matches'),
        where('tournamentId', '==', tournamentId),
        where('discipline', '==', discipline)
      );
      
      const matchesSnapshot = await getDocs(matchesQuery);
      
      return matchesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate()
      }));
    } catch (error) {
      logger.error('Erreur lors de la récupération des matchs:', error);
      throw error;
    }
  },
  
  // Mettre à jour un match
  async updateMatch(matchId, updateData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const matchRef = doc(db, 'matches', matchId);
      const matchDoc = await getDoc(matchRef);
      
      if (!matchDoc.exists()) {
        throw new Error('Match non trouvé');
      }
      
      const matchData = matchDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du match
      if (matchData.createdBy !== user.uid) {
        throw new Error('Vous n\'êtes pas autorisé à modifier ce match');
      }
      
      // Mettre à jour le match
      await updateDoc(matchRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      // Si le match est terminé (un vainqueur a été désigné), vérifier s'il faut générer le tour suivant
      if (updateData.status === 'completed' && updateData.winner) {
        // Vérifier si tous les matchs du même tour et de la même catégorie sont terminés
        await this.checkAndGenerateNextRound(matchData.tournamentId, matchData.discipline, matchData.category, matchData.round);
      }
      
      return { success: true, message: 'Match mis à jour avec succès' };
    } catch (error) {
      logger.error('Erreur lors de la mise à jour du match:', error);
      throw error;
    }
  },
  
  // Vérifier si tous les matchs d'un tour sont terminés et générer le tour suivant si nécessaire
  async checkAndGenerateNextRound(tournamentId, discipline, category, round) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Récupérer tous les matchs du tour actuel pour cette catégorie
      const matchesQuery = query(
        collection(db, 'matches'),
        where('tournamentId', '==', tournamentId),
        where('discipline', '==', discipline),
        where('category', '==', category),
        where('round', '==', round)
      );
      
      const matchesSnapshot = await getDocs(matchesQuery);
      const matches = matchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Vérifier si tous les matchs sont terminés
      const allCompleted = matches.every(match => match.status === 'completed' || match.status === 'bye');
      
      if (allCompleted && matches.length > 1) {
        // Générer les matchs du tour suivant
        await this.generateNextRoundMatches(tournamentId, discipline, category, round, matches);
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Erreur lors de la vérification des matchs:', error);
      throw error;
    }
  },
  
  // Générer les matchs du tour suivant
  async generateNextRoundMatches(tournamentId, discipline, category, currentRound, matches) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Récupérer les vainqueurs des matchs du tour actuel
      const winners = matches
        .filter(match => match.winner) // Filtrer les matchs avec un vainqueur
        .map(match => match.winner); // Récupérer les IDs des vainqueurs
      
      // S'il n'y a pas assez de vainqueurs, ne pas générer de tour suivant
      if (winners.length < 2) {
        return { success: false, message: 'Pas assez de vainqueurs pour générer le tour suivant' };
      }
      
      // Déterminer la phase du tournoi pour le tour suivant
      let nextPhase = '';
      const nextRound = currentRound + 1;
      
      if (winners.length === 2) {
        nextPhase = 'Finale';
      } else if (winners.length <= 4) {
        nextPhase = 'Demi-finale';
      } else if (winners.length <= 8) {
        nextPhase = 'Quart de finale';
      } else {
        nextPhase = `Tour ${nextRound}`;
      }
      
      // Récupérer les informations du tournoi pour les tatamis et l'heure
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      const tatamiCount = tournamentData.tatamiCount || 1;
      
      // Créer des paires de vainqueurs pour les matchs du tour suivant
      const nextRoundMatches = [];
      const now = new Date();
      
      // Calculer l'heure de début pour les matchs du tour suivant (30 minutes après l'heure actuelle)
      const startTime = new Date();
      startTime.setMinutes(startTime.getMinutes() + 30);
      const scheduledTime = startTime.getHours().toString().padStart(2, '0') + ':' + 
                          startTime.getMinutes().toString().padStart(2, '0');
      
      // Créer des paires de vainqueurs pour les matchs du tour suivant
      for (let i = 0; i < winners.length; i += 2) {
        const athlete1 = winners[i];
        const athlete2 = i + 1 < winners.length ? winners[i + 1] : null;
        
        // Attribuer un tatami en alternant
        const tatamiNumber = (i % tatamiCount) + 1;
        
        const match = {
          tournamentId,
          discipline,
          category,
          round: nextRound,
          phase: nextPhase,
          athlete1,
          athlete2,
          winner: athlete2 ? null : athlete1, // Si pas d'adversaire, l'athlète 1 est vainqueur par défaut
          status: athlete2 ? 'scheduled' : 'bye',
          tatamiNumber,
          scheduledTime,
          createdAt: now,
          updatedAt: now,
          createdBy: user.uid
        };
        
        // Ajouter le match à Firestore
        const matchRef = await addDoc(collection(db, 'matches'), match);
        nextRoundMatches.push({ id: matchRef.id, ...match });
      }
      
      return { success: true, matches: nextRoundMatches };
    } catch (error) {
      logger.error('Erreur lors de la génération des matchs du tour suivant:', error);
      throw error;
    }
  },
  
  // Créer un match manuellement
  async createMatch(matchData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Vérifier que le tournoi existe
      const tournamentRef = doc(db, 'tournaments', matchData.tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à ce tournoi');
      }
      
      // Ajouter le match à Firestore
      const now = new Date();
      const newMatch = {
        ...matchData,
        createdAt: now,
        updatedAt: now,
        createdBy: user.uid
      };
      
      const matchRef = await addDoc(collection(db, 'matches'), newMatch);
      
      // Mettre à jour le tournoi pour indiquer que des matchs ont été générés
      if (!tournamentData.matchesGenerated) {
        await updateDoc(tournamentRef, {
          matchesGenerated: true,
          updatedAt: now
        });
      }
      
      return { id: matchRef.id, ...newMatch };
    } catch (error) {
      logger.error('Erreur lors de la création du match:', error);
      throw error;
    }
  },
  
  // Réinitialiser les matchs d'un tournoi
  async resetMatches(tournamentId, discipline = null) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Vérifier que le tournoi existe
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Vérifier que l'utilisateur est le créateur du tournoi
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à ce tournoi');
      }
      
      // Préparer la requête pour récupérer les matchs à supprimer
      let matchesQuery;
      if (discipline) {
        // Supprimer uniquement les matchs de la discipline spécifiée
        matchesQuery = query(
          collection(db, 'matches'),
          where('tournamentId', '==', tournamentId),
          where('discipline', '==', discipline)
        );
      } else {
        // Supprimer tous les matchs du tournoi
        matchesQuery = query(
          collection(db, 'matches'),
          where('tournamentId', '==', tournamentId)
        );
      }
      
      // Récupérer tous les matchs à supprimer
      const matchesSnapshot = await getDocs(matchesQuery);
      
      // Supprimer chaque match
      const batch = writeBatch(db);
      let matchCount = 0;
      
      matchesSnapshot.forEach(doc => {
        batch.delete(doc.ref);
        matchCount++;
      });
      
      // Exécuter le batch delete
      if (matchCount > 0) {
        await batch.commit();
      }
      
      // Mettre à jour le statut du tournoi si tous les matchs ont été supprimés
      if (!discipline) {
        await updateDoc(tournamentRef, {
          matchesGenerated: false,
          updatedAt: new Date()
        });
      }
      
      return { success: true, message: `${matchCount} matchs supprimés avec succès` };
    } catch (error) {
      logger.error('Erreur lors de la réinitialisation des matchs:', error);
      throw error;
    }
  }
};

// Service athlète
export const athleteService = {
  // Créer un athlète
  async createAthlete(athleteData, tournamentId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const now = new Date();
      
      // Vérifier l'éligibilité de l'utilisateur pour ajouter un athlète
      const eligibilityCheck = await userService.checkPlanAccess('add_athletes');
      if (!eligibilityCheck.hasAccess) {
        throw new Error(`Vous avez atteint la limite d'athlètes pour votre abonnement (${eligibilityCheck.currentUsage}/${eligibilityCheck.limit}). Veuillez mettre à niveau votre abonnement pour ajouter plus d'athlètes.`);
      }
      
      // Préparer les données de l'athlète
      const newAthlete = {
        ...athleteData,
        tournaments: tournamentId ? [tournamentId] : [],
        createdBy: user.uid,
        createdAt: now,
        updatedAt: now
      };
      
      // Ajouter l'athlète à Firestore
      const athleteRef = await addDoc(collection(db, 'athletes'), newAthlete);
      const athleteId = athleteRef.id;
      
      // Mettre à jour le compteur d'athlètes dans le profil utilisateur
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const athletesCount = (userData.athletesCount || 0) + 1;
        
        await updateDoc(userRef, {
          athletesCount,
          updatedAt: now
        });
      }
      
      // Si un tournoi est spécifié, ajouter l'athlète au tournoi
      if (tournamentId) {
        const tournamentRef = doc(db, 'tournaments', tournamentId);
        const tournamentDoc = await getDoc(tournamentRef);
        
        if (tournamentDoc.exists()) {
          const tournamentData = tournamentDoc.data();
          const athletes = [...(tournamentData.athletes || []), athleteId];
          
          await updateDoc(tournamentRef, {
            athletes,
            updatedAt: now
          });
        }
      }
      
      return { id: athleteId, ...newAthlete };
    } catch (error) {
      logger.error('Erreur lors de la création de l\'athlète:', error);
      throw error;
    }
  },
  
  // Obtenir tous les athlètes de l'utilisateur connecté
  async getAllAthletes() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Ne récupérer que les athlètes créés par l'utilisateur connecté
      // Solution temporaire pour éviter l'erreur d'index manquant
      const athletesSnapshot = await getDocs(
        query(
          collection(db, 'athletes'), 
          where('createdBy', '==', user.uid)
          // orderBy('createdAt', 'desc') // Commenté temporairement jusqu'à la création de l'index
        )
      );
      
      // Tri manuel des résultats
      const athleteDocs = athletesSnapshot.docs.sort((a, b) => {
        const dateA = a.data().createdAt;
        const dateB = b.data().createdAt;
        
        // Convertir les timestamps en millisecondes pour comparaison
        const timeA = dateA && typeof dateA.toMillis === 'function' ? dateA.toMillis() : new Date(dateA).getTime();
        const timeB = dateB && typeof dateB.toMillis === 'function' ? dateB.toMillis() : new Date(dateB).getTime();
        
        return timeB - timeA; // Tri décroissant (plus récent d'abord)
      });
      
      return athleteDocs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function' ? data.updatedAt.toDate() : new Date(data.updatedAt)
        };
      });
    } catch (error) {
      logger.error('Erreur lors de la récupération des athlètes:', error);
      throw error;
    }
  },
  
  // Obtenir un athlète par ID
  async getAthleteById(athleteId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const athleteDoc = await getDoc(doc(db, 'athletes', athleteId));
      if (!athleteDoc.exists()) {
        return null;
      }
      
      const athleteData = athleteDoc.data();
      
      // Vérifier que l'utilisateur est bien le créateur de l'athlète
      if (athleteData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à cet athlète');
      }
      
      return {
        id: athleteDoc.id,
        ...athleteData,
        createdAt: athleteData.createdAt && typeof athleteData.createdAt.toDate === 'function' ? athleteData.createdAt.toDate() : new Date(athleteData.createdAt),
        updatedAt: athleteData.updatedAt && typeof athleteData.updatedAt.toDate === 'function' ? athleteData.updatedAt.toDate() : new Date(athleteData.updatedAt)
      };
    } catch (error) {
      logger.error('Erreur lors de la récupération de l\'athlète:', error);
      throw error;
    }
  },
  
  // Ajouter un athlète à un tournoi
  async addAthleteToTournament(athleteId, tournamentId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Vérifier si l'athlète existe
      const athleteRef = doc(db, 'athletes', athleteId);
      const athleteDoc = await getDoc(athleteRef);
      
      if (!athleteDoc.exists()) {
        throw new Error('Athlète non trouvé');
      }
      
      // Vérifier que l'utilisateur est bien le créateur de l'athlète
      const athleteData = athleteDoc.data();
      if (athleteData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à cet athlète');
      }
      
      // Vérifier si le tournoi existe
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        throw new Error('Tournoi non trouvé');
      }
      
      // Vérifier que l'utilisateur est bien le créateur du tournoi
      const tournamentData = tournamentDoc.data();
      if (tournamentData.createdBy !== user.uid) {
        throw new Error('Vous n\'avez pas accès à ce tournoi');
      }
      
      // Ajouter l'athlète au tournoi
      const athletes = [...(tournamentData.athletes || []), athleteId];
      await updateDoc(tournamentRef, {
        athletes,
        updatedAt: new Date()
      });
      
      // Mettre à jour l'athlète
      await updateDoc(athleteRef, {
        tournaments: [...(athleteData.tournaments || []), tournamentId],
        updatedAt: new Date()
      });
      
      return { success: true, message: 'Athlète ajouté au tournoi avec succès' };
    } catch (error) {
      logger.error('Erreur lors de l\'ajout de l\'athlète au tournoi:', error);
      throw error;
    }
  },
  
  // Mettre à jour un athlète
  async updateAthlete(athleteId, updateData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const athleteRef = doc(db, 'athletes', athleteId);
      const athleteDoc = await getDoc(athleteRef);
      
      if (!athleteDoc.exists()) {
        throw new Error('Athlète non trouvé');
      }
      
      const athleteData = athleteDoc.data();
      
      // Vérifier que l'utilisateur est le créateur de l'athlète
      if (athleteData.createdBy !== user.uid) {
        throw new Error('Vous n\'êtes pas autorisé à modifier cet athlète');
      }
      
      // Mettre à jour l'athlète
      await updateDoc(athleteRef, {
        ...updateData,
        updatedAt: new Date()
      });
      
      // Récupérer l'athlète mis à jour
      const updatedAthleteDoc = await getDoc(athleteRef);
      const updatedAthleteData = updatedAthleteDoc.data();
      
      return {
        id: athleteId,
        ...updatedAthleteData,
        createdAt: updatedAthleteData.createdAt.toDate(),
        updatedAt: updatedAthleteData.updatedAt.toDate()
      };
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de l\'athlète:', error);
      throw error;
    }
  },
  
  // Supprimer un athlète
  async deleteAthlete(athleteId) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      const athleteRef = doc(db, 'athletes', athleteId);
      const athleteDoc = await getDoc(athleteRef);
      
      if (!athleteDoc.exists()) {
        throw new Error('Athlète non trouvé');
      }
      
      const athleteData = athleteDoc.data();
      
      // Vérifier que l'utilisateur est le créateur de l'athlète
      if (athleteData.createdBy !== user.uid) {
        throw new Error('Vous n\'êtes pas autorisé à supprimer cet athlète');
      }
      
      // Retirer l'athlète de tous les tournois associés
      if (athleteData.tournaments && athleteData.tournaments.length > 0) {
        for (const tournamentId of athleteData.tournaments) {
          const tournamentRef = doc(db, 'tournaments', tournamentId);
          const tournamentDoc = await getDoc(tournamentRef);
          
          if (tournamentDoc.exists()) {
            const tournamentData = tournamentDoc.data();
            const athletes = tournamentData.athletes.filter(id => id !== athleteId);
            
            await updateDoc(tournamentRef, {
              athletes,
              updatedAt: new Date()
            });
          }
        }
      }
      
      // Mettre à jour le compteur d'athlètes dans le profil utilisateur
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const athletesCount = Math.max(0, (userData.athletesCount || 0) - 1);
        
        await updateDoc(userRef, {
          athletesCount,
          updatedAt: new Date()
        });
      }
      
      // Supprimer l'athlète
      await deleteDoc(athleteRef);
      
      return { success: true, message: 'Athlète supprimé avec succès' };
    } catch (error) {
      logger.error('Erreur lors de la suppression de l\'athlète:', error);
      throw error;
    }
  },
  
  // Importer plusieurs athlètes
  async importAthletes(athletesList, tournamentId) {
    try {
      // Vérifier si l'utilisateur est connecté
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      // Vérifier si l'utilisateur a atteint sa limite d'athlètes
      const { hasAccess, currentUsage, limit } = await userService.checkPlanAccess('add_athletes');
      const remainingSlots = limit - currentUsage;
      
      if (!hasAccess || remainingSlots <= 0) {
        throw new Error(`Vous avez atteint votre limite d'athlètes (${currentUsage}/${limit}). Veuillez mettre à niveau votre abonnement pour en ajouter davantage.`);
      }

      // Limiter le nombre d'athlètes à importer selon la limite restante
      const athletesToImport = athletesList.slice(0, remainingSlots);
      
      if (athletesToImport.length < athletesList.length) {
        logger.warn(`Seuls ${athletesToImport.length} athlètes sur ${athletesList.length} seront importés en raison de la limite de votre abonnement.`);
      }

      // Vérifier que le tournoi existe et appartient à l'utilisateur
      if (tournamentId) {
        const tournamentRef = doc(db, 'tournaments', tournamentId);
        const tournamentDoc = await getDoc(tournamentRef);

        if (!tournamentDoc.exists()) {
          throw new Error('Tournoi non trouvé');
        }

        const tournamentData = tournamentDoc.data();
        if (tournamentData.createdBy !== user.uid) {
          throw new Error('Vous n\'avez pas accès à ce tournoi');
        }
      }

      // Créer les athlètes et les ajouter au tournoi
      const importedAthletes = [];
      const athleteIds = [];
      const now = new Date();

      // Créer chaque athlète
      for (const athleteData of athletesToImport) {
        // Créer l'athlète dans Firestore
        const newAthlete = {
          ...athleteData,
          tournaments: tournamentId ? [tournamentId] : [],
          createdBy: user.uid,
          createdAt: now,
          updatedAt: now
        };
        
        const athleteRef = await addDoc(collection(db, 'athletes'), newAthlete);
        const athleteId = athleteRef.id;
        
        athleteIds.push(athleteId);
        importedAthletes.push({ id: athleteId, ...newAthlete });
      }

      // Mettre à jour le tournoi si nécessaire
      if (tournamentId && athleteIds.length > 0) {
        const tournamentRef = doc(db, 'tournaments', tournamentId);
        const tournamentDoc = await getDoc(tournamentRef);
        
        if (tournamentDoc.exists()) {
          const tournamentData = tournamentDoc.data();
          const athletes = [...(tournamentData.athletes || []), ...athleteIds];
          
          await updateDoc(tournamentRef, {
            athletes,
            updatedAt: now
          });
        }
      }

      // Mettre à jour le compteur d'athlètes dans le profil utilisateur
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const athletesCount = (userData.athletesCount || 0) + athleteIds.length;
        
        await updateDoc(userRef, {
          athletesCount,
          updatedAt: now
        });
      }

      return importedAthletes;
    } catch (error) {
      logger.error('Erreur lors de l\'importation des athlètes:', error);
      throw error;
    }
  }
};
